import {useEffect, useRef} from 'react'
import {isLoggedIn} from '@github-ui/client-env'
import {updateDockedPanelWidth} from './update-docked-panel-width'

export const SIDEBAR_STORAGE_KEY = 'diff-sidebar-width'

/**
 * Intercepts localStorage writes for the sidebar width key to sync resize
 * changes back to the server. The initial width is handled by the
 * `width.default` prop on `SplitPageLayout.Sidebar`; this hook only
 * persists user-initiated resizes.
 *
 * TODO: Replace with `onResizeEnd` callback, see: https://github.com/github/primer/issues/6425
 * This approach copies the `useFileTreeWidthSync`, which should also be refactored.
 *
 * @param serverWidth - The width value from the server, used to avoid re-syncing the same value
 * @param owner - The repository owner login
 * @param repo - The repository name
 * @param pullNumber - The pull request number
 */
export function useDockedPanelWidthSync(
  serverWidth: number | undefined | null,
  owner: string | undefined,
  repo: string | undefined,
  pullNumber: number | undefined,
): void {
  const lastSyncedWidthRef = useRef<number | undefined | null>(serverWidth)

  useEffect(() => {
    if (serverWidth !== undefined && serverWidth !== null) {
      lastSyncedWidthRef.current = serverWidth
    }
  }, [serverWidth])

  // Intercept localStorage writes to sync width changes back to server
  useEffect(() => {
    if (!owner || !repo || !pullNumber) return

    const originalSetItem = localStorage.setItem.bind(localStorage)

    localStorage.setItem = (key: string, value: string) => {
      originalSetItem(key, value)

      if (key !== SIDEBAR_STORAGE_KEY) return

      const newWidth = parseInt(value, 10)
      if (isNaN(newWidth) || newWidth <= 0) return

      // Only sync to server if the width actually changed
      if (newWidth !== lastSyncedWidthRef.current) {
        lastSyncedWidthRef.current = newWidth
        if (isLoggedIn()) {
          updateDockedPanelWidth(newWidth, owner, repo, pullNumber)
        }
      }
    }

    return () => {
      localStorage.setItem = originalSetItem
    }
  }, [owner, repo, pullNumber])
}
